'use client';

import { Check } from '@untitledui/icons';
import { isValidElement, useContext } from 'react';
import type { ListBoxItemProps as AriaListBoxItemProps } from 'react-aria-components/ListBox';
import { ListBoxItem as AriaListBoxItem } from 'react-aria-components/ListBox';
import { Text as AriaText } from 'react-aria-components/Text';

import { cx } from '../../../utils/cx';
import { isReactComponent } from '../../../utils/is-react-component';
import { Avatar } from '../avatar/avatar';
import type { SelectItemType } from './select';
import { SelectContext } from './select';

/**
 * @fileoverview Select item component for the TRM UI Kit.
 * @agents generated no-review
 *
 * Individual option item within select dropdowns with support for icons and descriptions.
 * Generated from UntitledUI.
 *
 * @see https://www.untitledui.com/components
 */

const sizes = {
  sm: 'p-2 pr-2.5',
  md: 'p-2.5 pl-2',
};

/**
 * Props for the SelectItem component.
 *
 * Represents an individual option within a select dropdown with support for
 * icons, avatars, and supporting text. This interface combines the React Aria
 * ListBoxItemProps with custom SelectItemType properties to provide a flexible
 * and accessible select item implementation.
 *
 * @property {string} id - Unique identifier for the select item
 * @property {string} [label] - Primary label text displayed for the item
 * @property {string} [avatarUrl] - Optional avatar image URL to display alongside the label
 * @property {boolean} [isDisabled] - Whether the item is disabled and cannot be selected
 * @property {string} [supportingText] - Optional secondary text displayed after the label
 * @property {FC | ReactNode} [icon] - Optional icon component or element to display before the label
 * @property {string | ((state) => ReactNode)} [children] - Content to render, can be a string or render function
 *
 * @see {@link SelectItem} - The component that uses these props
 * @see {@link SelectItemType} - The underlying data type for select items in [select.tsx](./select.tsx)
 * @see {@link Select} - The parent select component in [select.tsx](./select.tsx)
 *
 * @example
 * ```tsx
 * <SelectItem
 *   id="user-1"
 *   label="John Doe"
 *   supportingText="john@example.com"
 *   avatarUrl="https://example.com/avatar.jpg"
 * />
 * ```
 *
 * @example With icon
 * ```tsx
 * <SelectItem
 *   id="settings"
 *   label="Settings"
 *   icon={SettingsIcon}
 * />
 * ```
 */
export interface SelectItemProps
  extends Omit<AriaListBoxItemProps<SelectItemType>, 'id'>, SelectItemType {}

export const SelectItem = ({
  label,
  id,
  value,
  avatarUrl,
  supportingText,
  isDisabled,
  icon: Icon,
  className,
  children,
  ...props
}: SelectItemProps) => {
  const { size } = useContext(SelectContext);

  const labelOrChildren = label || (typeof children === 'string' ? children : '');
  const textValue = supportingText ? labelOrChildren + ' ' + supportingText : labelOrChildren;

  return (
    <AriaListBoxItem
      id={id}
      value={
        value ?? {
          id,
          label: labelOrChildren,
          avatarUrl,
          supportingText,
          isDisabled,
          icon: Icon,
        }
      }
      textValue={textValue}
      isDisabled={isDisabled}
      {...props}
      className={(state) =>
        cx(
          'w-full px-1.5 py-px outline-hidden',
          typeof className === 'function' ? className(state) : className,
        )
      }
    >
      {(state) => (
        <div
          className={cx(
            'flex cursor-pointer items-center gap-2 rounded-md outline-hidden select-none',
            state.isSelected && 'bg-active',
            state.isDisabled && 'cursor-not-allowed',
            state.isFocused && 'bg-primary_hover',
            state.isFocusVisible && 'ring-2 ring-focus-ring ring-inset',

            // Icon styles
            '*:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary',
            state.isDisabled && '*:data-icon:text-fg-disabled',

            sizes[size],
          )}
        >
          {avatarUrl ? (
            <Avatar aria-hidden="true" size="xs" src={avatarUrl} alt={label} />
          ) : isReactComponent(Icon) ? (
            <Icon data-icon aria-hidden="true" />
          ) : isValidElement(Icon) ? (
            Icon
          ) : null}

          <div className="flex w-full min-w-0 flex-1 flex-wrap gap-x-2">
            <AriaText
              slot="label"
              className={cx(
                'truncate text-md font-medium whitespace-nowrap text-primary',
                state.isDisabled && 'text-disabled',
              )}
            >
              {label || (typeof children === 'function' ? children(state) : children)}
            </AriaText>

            {supportingText && (
              <AriaText
                slot="description"
                className={cx(
                  'text-md whitespace-nowrap text-tertiary',
                  state.isDisabled && 'text-disabled',
                )}
              >
                {supportingText}
              </AriaText>
            )}
          </div>

          {state.isSelected && (
            <Check
              aria-hidden="true"
              className={cx(
                'ml-auto text-fg-brand-primary',
                size === 'sm' ? 'size-4 stroke-[2.5px]' : 'size-5',
                state.isDisabled && 'text-fg-disabled',
              )}
            />
          )}
        </div>
      )}
    </AriaListBoxItem>
  );
};
